import { PdfReportType } from 'api/Data/types';
import { ModelApiName } from 'api/Exec/types';
import PdfReportTypesSelect from 'app/common_components/PdfFormData/PdfReportTypesSelect';
import usePdfPrsReportTypes from 'app/hooks/usePdfPrsReportTypes';
import { SelectOption } from 'app/types/Forms';
import { useAppSelector } from 'store/hooks';
import { selectOrgModels } from 'store/orgs/orgsSlice';
import { selectMyUser } from 'store/users/usersSlice';

import { Collapse, DatePicker, Form, FormInstance, Input, Select } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';

const renderPrsItems = (isLoading: boolean, prsClinicalEnabled: boolean, isSexRequired: boolean) => {
  const sexOptions: SelectOption[] = [ {
    label: 'Female',
    value: 'Female',
  }, {
    label: 'Male',
    value: 'Male',
  } ];

  return (
    <>
      <Form.Item name={[ 'pdfData', 'patientName' ]} label="Patient name">
        <Input placeholder="Enter patient name" disabled={isLoading} />
      </Form.Item>

      <Form.Item name={[ 'pdfData', 'dob' ]} label="Date of birth">
        <DatePicker format="YYYY/MM/DD" placeholder="Select date of birth" disabled={isLoading} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item name={[ 'pdfData', 'sex' ]} label="Biological sex" rules={[ { required: isSexRequired, message: 'Field is mandatory' } ]}>
        <Select options={sexOptions} placeholder="Select biological sex" disabled={isLoading} />
      </Form.Item>

      {prsClinicalEnabled && (
        <>
          <Form.Item name={[ 'pdfData', 'patientId' ]} label="Patient id">
            <Input placeholder="Enter patient id" disabled={isLoading} />
          </Form.Item>

          <Form.Item name={[ 'pdfData', 'accession' ]} label="Accession No">
            <Input placeholder="Enter accession no" disabled={isLoading} />
          </Form.Item>

          <Form.Item name={[ 'pdfData', 'sampleId' ]} label="Sample id">
            <Input placeholder="Enter sample id" disabled={isLoading} />
          </Form.Item>

          <Form.Item name={[ 'pdfData', 'collected' ]} label="Sample collected">
            <DatePicker format="YYYY/MM/DD" placeholder="Select collected date" disabled={isLoading} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name={[ 'pdfData', 'received' ]} label="Sample received">
            <DatePicker format="YYYY/MM/DD" placeholder="Select received date" disabled={isLoading} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name={[ 'pdfData', 'specimenType' ]} label="Specimen type">
            <Input placeholder="Enter specimen type" disabled={isLoading} />
          </Form.Item>

          <Form.Item name={[ 'pdfData', 'providerName' ]} label="Provider name">
            <Input placeholder="Enter provider name" disabled={isLoading} />
          </Form.Item>

          <Form.Item name={[ 'pdfData', 'referringFacility' ]} label="Referring facility">
            <Input placeholder="Enter referring facility" disabled={isLoading} />
          </Form.Item>

          <Form.Item name={[ 'pdfData', 'address' ]} label="Address">
            <Input placeholder="Enter address" disabled={isLoading} />
          </Form.Item>
        </>
      )}
    </>
  );
};

const renderVaderItems = (isLoading: boolean) => {
  return (
    <>
      <Form.Item name={[ 'pdfData', 'patientName' ]} label="Patient name">
        <Input placeholder="Enter patient name" disabled={isLoading} />
      </Form.Item>

      <Form.Item name={[ 'pdfData', 'patientId' ]} label="Patient id">
        <Input placeholder="Enter patient id" disabled={isLoading} />
      </Form.Item>
    </>
  );
};

interface Props {
  isLoading: boolean;
  modelName: string;
  form: FormInstance;
}

const PdfFormData: React.FC<Props> = ({ isLoading, modelName, form }) => {
  const selectedReportTypes = Form.useWatch('reportTypes', form) as PdfReportType[];
  const isSexRequired = !!selectedReportTypes &&
    (selectedReportTypes.includes('PRS_CLINICAL_CANCER') || selectedReportTypes.includes('PRS_RUO_CANCER'));
  const orgModels = useAppSelector(selectOrgModels);
  const { org: { reportAncestryEnabled } } = useAppSelector(selectMyUser);
  const { reportTypes } = usePdfPrsReportTypes();
  const [ modelApiName, setModelApiName ] = useState<ModelApiName | null>(null);
  const prsClinicalEnabled = reportTypes.includes('PRS_CLINICAL_CARDIO') || reportTypes.includes('PRS_CLINICAL_CANCER');

  const containPdfData: boolean = useMemo((): boolean => {
    const model = orgModels.filter(orgModel => orgModel.modelName === modelName)[0];

    if (model) {
      setModelApiName(model.modelApiName);

      if (model.modelApiName === 'vader' && reportAncestryEnabled) {
        return true;
      }

      if (model.modelApiName === 'mysterio' && reportTypes.length > 0) {
        return true;
      }
    }

    return false;
  }, [ modelName, orgModels, reportTypes, reportAncestryEnabled ]);

  const renderChildren = useCallback((modelApiName: ModelApiName | null) => {
    if (modelApiName === 'mysterio') {
      return renderPrsItems(isLoading, prsClinicalEnabled, isSexRequired);
    } else if (modelApiName === 'vader') {
      return renderVaderItems(isLoading);
    }

    return null;
  }, [ isLoading, prsClinicalEnabled, isSexRequired ]);

  return containPdfData ? (
    <>
      {modelApiName && <PdfReportTypesSelect isLoading={isLoading} modelApiName={modelApiName} />}

      <Collapse
        defaultActiveKey={[ 'pdfData' ]}
        items={[
          {
            key: 'pdfData',
            label: 'PDF data',
            children: renderChildren(modelApiName),
          },
        ]}
        style={{ marginBottom: 24 }}
      />
    </>
  ) : null;
};

export default PdfFormData;
